import React, { useMemo, useState } from 'react';
import type { UploadFile } from 'antd';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Input,
  message,
  Progress,
  Row,
  Select,
  Slider,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  Upload,
} from 'antd';
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  FashionMotionAspectRatio,
  FashionMotionQualityPreset,
  FashionMotionScenarioPreset,
  FashionMotionSourceType,
  FashionMotionJobResult,
  ProductMediaService,
} from '../services/productMediaService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const QUALITY_OPTIONS: { label: string; value: FashionMotionQualityPreset; description: string }[] = [
  { label: 'Draft', value: 'draft', description: 'Hızlı önizleme, düşük maliyet' },
  { label: 'Balanced', value: 'balanced', description: 'Hız / kalite dengesi' },
  { label: 'Quality', value: 'quality', description: 'Yüksek kalite üretim' },
  { label: 'Premium', value: 'premium', description: 'En yüksek kalite' },
];

const SCENARIO_OPTIONS: { label: string; value: FashionMotionScenarioPreset; description: string }[] = [
  { label: 'Editorial', value: 'editorial', description: 'Dergi estetiği, premium motion' },
  { label: 'Runway', value: 'runway', description: 'Podyum hissi, güçlü fashion energy' },
  { label: 'E-commerce', value: 'ecommerce', description: 'Temiz ürün odağı, sakin hareket' },
  { label: 'Lifestyle', value: 'lifestyle', description: 'Doğal ortam, markalı yaşam hissi' },
];

const ASPECT_RATIO_OPTIONS: FashionMotionAspectRatio[] = ['9:16', '1:1', '16:9'];
const SOURCE_TYPES: { label: string; value: FashionMotionSourceType; description: string }[] = [
  { label: 'Full body fashion image', value: 'full_body', description: 'Boydan çekim için önerilir' },
  { label: 'Catalog / flat image', value: 'catalog', description: 'Katalog / düz ürün görseli için' },
];

const defaultNegativePrompt =
  'garment change, product mutation, color shift, logo distortion, duplicate clothing, warped sleeves, extra accessories, blurry face, deformed body';

const FashMotionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const brandId = user?.brandId;

  const [sourceType, setSourceType] = useState<FashionMotionSourceType>('full_body');
  const [qualityPreset, setQualityPreset] = useState<FashionMotionQualityPreset>('balanced');
  const [scenarioPreset, setScenarioPreset] = useState<FashionMotionScenarioPreset>('editorial');
  const [aspectRatio, setAspectRatio] = useState<FashionMotionAspectRatio>('9:16');
  const [duration, setDuration] = useState(6);
  const [strictProductLock, setStrictProductLock] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState(defaultNegativePrompt);
  const [imageBase64, setImageBase64] = useState<string>('');
  const [previewImage, setPreviewImage] = useState<string>('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [job, setJob] = useState<FashionMotionJobResult | null>(null);
  const [running, setRunning] = useState(false);
  const [progressLabel, setProgressLabel] = useState('Beklemede');

  const canGenerate = useMemo(() => Boolean(imageBase64 || previewImage), [imageBase64, previewImage]);

  const readFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      setImageBase64(result);
      setPreviewImage(result);
    };
    reader.readAsDataURL(file);
  };

  const handleBeforeUpload = (file: any) => {
    setFileList([{ uid: file.name, name: file.name, status: 'done', originFileObj: file }]);
    void readFile(file);
    return false;
  };

  const handleReset = () => {
    setFileList([]);
    setImageBase64('');
    setPreviewImage('');
    setJob(null);
    setProgressLabel('Beklemede');
  };

  const updateProgressFromStatus = (state: FashionMotionJobResult) => {
    setJob(state);

    switch (state.status) {
      case 'queued':
        setProgressLabel('Kuyrukta');
        break;
      case 'normalizing':
        setProgressLabel('Görsel normalize ediliyor');
        break;
      case 'processing':
        setProgressLabel(`Video üretiliyor (${state.model || 'FAL'})`);
        break;
      case 'retrying':
        setProgressLabel('Fallback denemesi çalışıyor');
        break;
      case 'completed':
        setProgressLabel('Tamamlandı');
        break;
      case 'failed':
        setProgressLabel('Başarısız');
        break;
      default:
        setProgressLabel(String(state.status || 'Processing'));
    }
  };

  const handleGenerate = async () => {
    if (!imageBase64) {
      message.warning('Lütfen bir kıyafet görseli yükleyin.');
      return;
    }

    try {
      setRunning(true);
      setJob(null);
      setProgressLabel('İstek hazırlanıyor');

      const created = await ProductMediaService.createFashionMotionJob({
        image: imageBase64,
        sourceType,
        qualityPreset,
        duration,
        scenarioPreset,
        aspectRatio,
        prompt,
        negativePrompt,
        strictProductLock,
      });

      const finalJob = await ProductMediaService.pollFashionMotionJob(created.jobId, {
        intervalMs: qualityPreset === 'draft' ? 2500 : 4000,
        timeoutMs: duration >= 10 ? 15 * 60 * 1000 : 10 * 60 * 1000,
        onTick: updateProgressFromStatus,
      });

      setJob(finalJob);
      if (finalJob.status === 'completed') {
        message.success('Video üretildi.');
      } else {
        message.error(finalJob.error || 'Video üretimi başarısız oldu.');
      }
    } catch (error: any) {
      message.error(error?.message || 'Video üretimi başlatılamadı.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1480, margin: '0 auto' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
            Geri dön
          </Button>
          <Card
            style={{
              borderRadius: 20,
              border: 'none',
              background: 'linear-gradient(135deg, #111827 0%, #312e81 45%, #7c3aed 100%)',
              color: 'white',
            }}
            bodyStyle={{ padding: 28 }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} lg={15}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Tag color="purple" style={{ width: 'fit-content', marginBottom: 8 }}>
                    FashMotion • FAL Image to Video
                  </Tag>
                  <Title level={2} style={{ color: 'white', margin: 0 }}>
                    Kıyafet görselinden ürün-korumalı video üret
                  </Title>
                  <Paragraph style={{ color: 'rgba(255,255,255,0.86)', fontSize: 15, marginBottom: 0 }}>
                    Tek bir boydan kıyafet görselini yükleyin, kalite/süre/konsept seçin ve FAL tabanlı
                    pipeline ile aynı ürün kimliğini koruyarak video üretin. Bu akış katalog ve flat ürün
                    görselleri için de tekrar kullanılabilir.
                  </Paragraph>
                </Space>
              </Col>
              <Col xs={24} lg={9} style={{ textAlign: 'right' }}>
                <Space direction="vertical" size={10}>
                  <Tag color="geekblue">Seedance 2 • ana model</Tag>
                  <Tag color="gold">Kling fallback</Tag>
                  <Tag color="green">Nano Banana normalize</Tag>
                </Space>
              </Col>
            </Row>
          </Card>
        </div>

        {!brandId && (
          <Alert
            type="warning"
            showIcon
            message="Brand ID bulunamadı"
            description="Video üretimi için aktif bir marka bağlanmış kullanıcı gerekiyor."
          />
        )}

        <Row gutter={[24, 24]}>
          <Col xs={24} xl={14}>
            <Card title="1. Görsel ve istek ayarları" style={{ borderRadius: 18 }}>
              <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <div>
                  <Text strong>Kaynak görsel</Text>
                  <div style={{ marginTop: 8 }}>
                    <Upload.Dragger
                      accept="image/*"
                      beforeUpload={handleBeforeUpload}
                      fileList={fileList}
                      maxCount={1}
                      onRemove={() => {
                        handleReset();
                        return true;
                      }}
                      style={{ padding: '16px 0' }}
                    >
                      <p className="ant-upload-drag-icon">
                        <CloudUploadOutlined />
                      </p>
                      <p className="ant-upload-text">Kıyafet görselini sürükleyin veya seçin</p>
                      <p className="ant-upload-hint">
                        PNG / JPG / WEBP desteklenir. Boydan çekim veya katalog görseli yükleyebilirsiniz.
                      </p>
                    </Upload.Dragger>
                  </div>
                </div>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Text strong>Kaynak tipi</Text>
                    <Select
                      value={sourceType}
                      onChange={(value) => setSourceType(value)}
                      style={{ width: '100%', marginTop: 8 }}
                      options={SOURCE_TYPES.map((item) => ({
                        label: (
                          <Space direction="vertical" size={0}>
                            <span>{item.label}</span>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.description}
                            </Text>
                          </Space>
                        ),
                        value: item.value,
                      }))}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Text strong>Aspect ratio</Text>
                    <Select
                      value={aspectRatio}
                      onChange={setAspectRatio}
                      style={{ width: '100%', marginTop: 8 }}
                      options={ASPECT_RATIO_OPTIONS.map((item) => ({ label: item, value: item }))}
                    />
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Text strong>Kalite</Text>
                    <Select
                      value={qualityPreset}
                      onChange={setQualityPreset}
                      style={{ width: '100%', marginTop: 8 }}
                      options={QUALITY_OPTIONS.map((item) => ({
                        value: item.value,
                        label: (
                          <Space direction="vertical" size={0}>
                            <span>{item.label}</span>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.description}
                            </Text>
                          </Space>
                        ),
                      }))}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Text strong>Konsept / senaryo</Text>
                    <Select
                      value={scenarioPreset}
                      onChange={setScenarioPreset}
                      style={{ width: '100%', marginTop: 8 }}
                      options={SCENARIO_OPTIONS.map((item) => ({
                        value: item.value,
                        label: (
                          <Space direction="vertical" size={0}>
                            <span>{item.label}</span>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.description}
                            </Text>
                          </Space>
                        ),
                      }))}
                    />
                  </Col>
                </Row>

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text strong>Video süresi</Text>
                    <Tag>{duration} saniye</Tag>
                  </Space>
                  <Slider
                    min={3}
                    max={12}
                    step={1}
                    value={duration}
                    onChange={setDuration}
                    marks={{ 3: '3s', 6: '6s', 9: '9s', 12: '12s' }}
                  />
                </div>

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <div>
                      <Text strong>Strict product lock</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Ürün kimliğini olabildiğince sabit tutar.
                        </Text>
                      </div>
                    </div>
                    <Switch checked={strictProductLock} onChange={setStrictProductLock} />
                  </Space>
                </div>

                <div>
                  <Text strong>Ek prompt</Text>
                  <TextArea
                    rows={4}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Örn: studio lighting, soft motion, premium look, zoom yapma"
                    style={{ marginTop: 8 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                    İpucu: kamera sabit kalsın, zoom yapma, ürün kimliği değişmesin.
                  </Text>
                </div>

                <div>
                  <Text strong>Negative prompt</Text>
                  <TextArea
                    rows={3}
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                </div>

                <Space wrap>
                  <Button
                    type="primary"
                    icon={running ? <Spin size="small" /> : <PlayCircleOutlined />}
                    onClick={handleGenerate}
                    disabled={!canGenerate || running || !brandId}
                    size="large"
                  >
                    Videoyu üret
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={handleReset} disabled={running}>
                    Reset
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>

          <Col xs={24} xl={10}>
            <Card title="2. Önizleme ve sonuç" style={{ borderRadius: 18, minHeight: 620 }}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt="preview"
                    style={{ width: '100%', borderRadius: 16, objectFit: 'cover', border: '1px solid #f0f0f0' }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: 360,
                      borderRadius: 16,
                      border: '1px dashed #d9d9d9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#8c8c8c',
                      background: '#fafafa',
                    }}
                  >
                    Görsel önizlemesi burada görünecek
                  </div>
                )}

                <Card size="small" style={{ borderRadius: 14, background: '#fafafa' }}>
                  <Space direction="vertical" size={4}>
                    <Text strong>Durum</Text>
                    <Text>{progressLabel}</Text>
                    {job && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Model: {job.model || '—'} • Stage: {job.stage || job.status}
                      </Text>
                    )}
                  </Space>
                </Card>

                {job?.status && (
                  <Progress
                    percent={
                      job.status === 'completed'
                        ? 100
                        : job.status === 'failed'
                          ? 100
                          : job.status === 'retrying'
                            ? 85
                            : job.status === 'processing'
                              ? 65
                              : job.status === 'normalizing'
                                ? 35
                                : 15
                    }
                    status={job.status === 'failed' ? 'exception' : job.status === 'completed' ? 'success' : 'active'}
                  />
                )}

                {job?.normalizedImageUrl && (
                  <Alert
                    type="info"
                    showIcon
                    message="Normalize edilmiş referans hazır"
                    description={<a href={job.normalizedImageUrl} target="_blank" rel="noreferrer">Yeni referansı aç</a>}
                  />
                )}

                {job?.videoUrl ? (
                  <Card size="small" title="Üretilen video" style={{ borderRadius: 14 }}>
                    <video src={job.videoUrl} controls style={{ width: '100%', borderRadius: 12 }} />
                    <Divider style={{ margin: '16px 0' }} />
                    <Button block onClick={() => window.open(job.videoUrl, '_blank', 'noopener,noreferrer')}>
                      Videoyu yeni sekmede aç
                    </Button>
                  </Card>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message="Video henüz üretilmedi"
                    description="Görsel yükledikten sonra üretim başlatabilirsiniz."
                  />
                )}

                {job?.error && (
                  <Alert type="error" showIcon message="Üretim hatası" description={job.error} />
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default FashMotionPage;
